import React, { useEffect, useState } from 'react';
import { TouchableOpacity } from 'react-native';

import { Box, ShadowBox, Text } from '@components/Restyle';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import { useHover } from '@hooks/useHover';

export type ProjectFilesFilterPopupProps = {
  onSelectMedia?: (selected: boolean) => void;
  onSelectDocument?: (selected: boolean) => void;
  onSearchChange?: (searchText: string) => void;
  onClearAll?: () => void;
  mediaSelected?: boolean;
  documentSelected?: boolean;
  searchValue?: string;
};

export const PopupProjectFilesFilter: React.FC<
  ProjectFilesFilterPopupProps
> = ({
  onSelectMedia,
  onSelectDocument,
  onSearchChange,
  onClearAll,
  mediaSelected = false,
  documentSelected = false,
  searchValue = '',
}) => {
  const [search, setSearch] = useState<string>(searchValue);
  const { isHovered: isMediaHovered, hoverProps: mediaHoverProps } = useHover();
  const { isHovered: isDocumentHovered, hoverProps: documentHoverProps } =
    useHover();
  const { isHovered: isClearAllHovered, hoverProps: clearAllHoverProps } =
    useHover();

  // Sync internal search state with searchValue prop when it changes
  useEffect(() => {
    setSearch(searchValue);
  }, [searchValue]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    onSearchChange?.(value);
  };

  const enabled = search.length > 0 || mediaSelected || documentSelected;

  return (
    <ShadowBox
      width={300}
      flex={1}
      borderWidth={1}
      alignItems='flex-start'
      borderRadius='xs'
      backgroundColor='white'>
      {/* Search Input */}
      <Box
        marginHorizontal='m'
        marginTop='m'
        style={{
          width: 268,
          backgroundColor: 'grey01',
        }}
        justifyContent='center'
        flexDirection='row'
        alignItems='flex-start'>
        <SearchInput
          value={search}
          height={40}
          onTextChanged={handleSearchChange}
          placeHolder='Search uploader'
        />
      </Box>

      {/* Divider */}
      <Box
        style={{ width: '100%' }}
        height={1}
        backgroundColor='grey02'
        marginVertical='s'
      />

      {/* Media Checkbox */}
      <TouchableOpacity
        onPress={() => onSelectMedia?.(!mediaSelected)}
        activeOpacity={0.7}
        accessibilityLabel='Media'>
        <Box
          width={300}
          flexDirection='row'
          alignItems='center'
          paddingHorizontal='m'
          paddingVertical='s'
          style={{ paddingVertical: 9 }}
          backgroundColor={isMediaHovered ? 'grey01' : 'white'}
          {...mediaHoverProps}>
          <Box marginRight='xs' justifyContent='center'>
            <CheckBoxButton checked={mediaSelected} />
          </Box>
          <Text variant='bodySecondary' color='textPrimary'>
            Media
          </Text>
        </Box>
      </TouchableOpacity>

      {/* Document Checkbox */}
      <TouchableOpacity
        onPress={() => onSelectDocument?.(!documentSelected)}
        activeOpacity={0.7}
        accessibilityLabel='Document'>
        <Box
          width={300}
          flexDirection='row'
          alignItems='center'
          paddingHorizontal='m'
          paddingVertical='s'
          style={{ paddingVertical: 9 }}
          backgroundColor={isDocumentHovered ? 'grey01' : 'white'}
          {...documentHoverProps}>
          <Box marginRight='xs' justifyContent='center'>
            <CheckBoxButton checked={documentSelected} />
          </Box>
          <Text variant='bodySecondary' color='textPrimary'>
            Document
          </Text>
        </Box>
      </TouchableOpacity>

      {/* Divider */}
      <Box
        style={{ width: '100%' }}
        height={1}
        backgroundColor='grey02'
        marginTop='s'
      />

      {/* Clear All Button */}
      <TouchableOpacity
        onPress={onClearAll}
        activeOpacity={0.7}
        accessibilityLabel='Clear All'
        style={{ flex: 1, width: '100%' }}>
        <Box
          alignItems='center'
          paddingVertical='m'
          backgroundColor={isClearAllHovered && enabled ? 'grey01' : 'white'}
          {...clearAllHoverProps}>
          <Text variant='bodySecondary' color={enabled ? 'grey05' : 'grey04'}>
            Clear All
          </Text>
        </Box>
      </TouchableOpacity>
    </ShadowBox>
  );
};
